import React, { useEffect, useMemo, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import Button from 'design-system/Button';
import { rem } from 'design-system/values';
import Text from 'design-system/Text';
import LogoBankAccount from 'design-system/LogoBankAccount';
import BlurAmountView from 'design-system/BlurAmountView';
import useColors from 'hooks/useColors';
import normalize from 'common/Normalize';
import { useAppSelector } from 'store/hooks';
import { selectIsScrambled } from 'hooks/useScrambleMode';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import EditTransactionSelect from 'features/transactions/components/multiple/EditTransactionSelect';
import { IconBoldAdd } from 'design-system/icons/IconBoldAdd';
import { Spacer } from 'design-system';
import { IconBoldSubtract } from 'design-system/icons/IconBoldSubtract';
import { SpaceUserAvatar } from 'design-system/SpaceUserAvatar';
import { isBankAccountBroken } from 'features/settings/logins/utils';
import { formatSortCode } from 'utils/formatting';
import { isCreditReportAccount } from 'utils/creditReportAccounts';
import { isWeb } from '../../../constants';
import RowAccountBalance from './RowAccountBalance';
import RowAccountGoalInfo from './RowAccountGoalInfo';
import RowAccountGoalProgress from './RowAccountGoalProgress';
/**
 * If you need to show this within a card, use `onCard`, which makes logo icons use darker background in dark mode.
 */
const RowAccount = ({ data, disabled, disabledOpacity, style, selected, isSelectingAccounts, primaryBalanceOnly, onCard, small, hideLogo, onPress, onRowLongPressed, onOpenLogins, onOpenUpgrade, forceShowAction, hideActionRequiredText, logo, showHidden, isMultipleSelect, plusIcon = false, minusIcon = false, showGoal, hideSpaceUserAvatar = false, preTitle, notSupportedText, showBalance = true, forceScramble, }) => {
    const colors = useColors();
    const disableCTA = useRef(false);
    const touchViewRef = useRef(null);
    const timeoutRef = useRef(undefined);
    const scrambled = useAppSelector(selectIsScrambled);
    const goal = useAppSelector((store) => store.accounts?.goals?.[data.id]);
    useEffect(() => () => {
        clearTimeout(timeoutRef.current);
    }, []);
    const { isUnlocked } = useShouldShowUpgrade({
        benefitId: 'savingGoals',
    });
    const showRenewAccess = isBankAccountBroken(data);
    const isInvestmentAccountWithGoals = data?.type === 'INVESTMENT' ||
        (data?.type === 'CRYPTO' && data?.nativeBalance);
    const shouldShowRenewAccessFinal = showRenewAccess && (!disabled || forceShowAction) && onOpenLogins;
    const shouldShowActivateOrUpgrade = onOpenUpgrade &&
        (data.ultimatePreviewAccount ||
            data.proPreviewAccount ||
            data.deactivatedOverQuota);
    const shouldShowGoalsProgress = showGoal &&
        isUnlocked &&
        goal &&
        (data.type === 'SAVINGS' ||
            data.type === 'PENSION' ||
            isInvestmentAccountWithGoals) &&
        !shouldShowRenewAccessFinal &&
        !shouldShowActivateOrUpgrade;
    const editingSpace = plusIcon || minusIcon;
    const openLogins = () => {
        if (disableCTA.current) {
            return;
        }
        disableCTA.current = true;
        if (onOpenLogins) {
            onOpenLogins(data, touchViewRef);
        }
        timeoutRef.current = setTimeout(() => {
            disableCTA.current = false;
        }, 1000);
    };
    const openUpgrade = () => {
        if (onOpenUpgrade)
            onOpenUpgrade(data, touchViewRef);
    };
    const pressed = () => {
        if (onPress)
            onPress(data, touchViewRef);
    };
    const longPressed = () => {
        if (onRowLongPressed)
            onRowLongPressed(data, touchViewRef);
    };
    const renderSelect = () => {
        if (!isSelectingAccounts)
            return null;
        return (<View style={styles.selectWrap}>
        <EditTransactionSelect selected={selected} style={styles.checkOnSquircle} isMultipleSelect={isMultipleSelect}/>
      </View>);
    };
    const renderAccountBalance = () => {
        if (shouldShowActivateOrUpgrade) {
            return (<View style={styles.buttonContainer} pointerEvents={isWeb ? 'none' : 'auto'}>
          <Button small square errorReversed containerStyle={styles.renewButton} onPress={openUpgrade} title={data.deactivatedOverQuota ? 'Activate' : 'Upgrade'}/>
        </View>);
        }
        if (shouldShowRenewAccessFinal) {
            return (<View style={styles.buttonContainer}>
          <Button small square errorReversed containerStyle={styles.renewButton} onPress={openLogins} title="Renew access"/>
        </View>);
        }
        if (notSupportedText) {
            return (<Text Text-14 Gray>
          {notSupportedText}
        </Text>);
        }
        if (showBalance) {
            return (<RowAccountBalance data={data} primaryBalanceOnly={primaryBalanceOnly} small={small} forceScramble={forceScramble}/>);
        }
        return null;
    };
    const SpaceOwner = useMemo(() => !(hideSpaceUserAvatar || plusIcon || minusIcon) ? (<SpaceUserAvatar key={`${data.id}SpaceInfo`} userId={data.userId} spaceId={data.spaceId} xxSmall style={styles.spaceUserAvatar}/>) : null, [
        data.id,
        data.spaceId,
        data.userId,
        hideSpaceUserAvatar,
        minusIcon,
        plusIcon,
    ]);
    const { renderDetails, didRenderAccountOwnerInfo } = useMemo(() => {
        let { sortCode, accountNumber } = data;
        if (isCreditReportAccount(data) &&
            (data.type === 'LOAN' || data.type === 'MORTGAGE')) {
            sortCode = undefined;
            accountNumber = null;
        }
        if (sortCode) {
            sortCode = formatSortCode(sortCode);
        }
        if (data.type !== 'CREDITCARD' &&
            accountNumber &&
            accountNumber.length === 4) {
            accountNumber = `•••••• ${data.accountNumber}`;
        }
        if (data.type === 'CREDITCARD' &&
            accountNumber &&
            accountNumber.length === 4) {
            accountNumber = `•••• •••• •••• ${data.accountNumber}`;
        }
        if (showRenewAccess && !hideActionRequiredText) {
            return {
                renderDetails: [
                    <Text TextThin-14 numberOfLines={1} ellipsizeMode="tail" Gray key={`${data.id}-action-required`}>
            Action required
          </Text>,
                    !shouldShowGoalsProgress ? SpaceOwner : null,
                ],
                didRenderAccountOwnerInfo: !shouldShowGoalsProgress,
            };
        }
        if (!sortCode && !accountNumber && !shouldShowGoalsProgress)
            return {
                renderDetails: SpaceOwner,
                didRenderAccountOwnerInfo: true,
            };
        return {
            renderDetails: [
                (!!sortCode || !!accountNumber) && (<BlurAmountView key={`${data.id}${sortCode}${accountNumber}`} enabled={scrambled}>
            <Text TextThin-14 numberOfLines={1} ellipsizeMode="tail" Gray>
              {`${sortCode ? `${sortCode} ` : ''}${accountNumber || ''}`}
            </Text>
          </BlurAmountView>),
                shouldShowGoalsProgress ? (<RowAccountGoalInfo key={`${data.id}RowAccountGoalInfo`} accountId={data.id}/>) : (SpaceOwner),
            ],
            didRenderAccountOwnerInfo: !shouldShowGoalsProgress,
        };
    }, [
        SpaceOwner,
        data,
        hideActionRequiredText,
        scrambled,
        shouldShowGoalsProgress,
        showRenewAccess,
    ]);
    const icon = !selected
        ? logo ||
            (hideLogo ? null : (<LogoBankAccount size="R44" onCard={onCard} data={data} showHidden={showHidden}/>))
        : renderSelect();
    const primaryColor = useMemo(() => {
        if (data.isHidden && showHidden) {
            return { Gray: true };
        }
        return { OffBlack: true };
    }, [data.isHidden, showHidden]);
    const content = (<TouchableHighlight hoverOnLight={!editingSpace} onCard disabled={disabledOpacity || disabled} underlayColor={colors.background.underlay} activeOpacity={1} onLongPress={longPressed} onPress={pressed}>
      <View style={[styles.rowAccount, style]}>
        {plusIcon ? (<>
            <View style={styles.buttonContainer}>
              <IconBoldAdd color={colors.elements.positive}/>
            </View>
            <Spacer w={16}/>
          </>) : null}

        {minusIcon ? (<>
            <View style={styles.buttonContainer}>
              <IconBoldSubtract color={colors.elements.negative}/>
            </View>
            <Spacer w={16}/>
          </>) : null}

        {icon}
        <View style={styles.container}>
          <View style={[
            styles.infoContainer,
            shouldShowGoalsProgress && styles.infoContainerWithGoal,
        ]}>
            <View style={[
            styles.textContainer,
            hideLogo && styles.textContainerSmall,
        ]}>
              {!!preTitle && preTitle}
              <Text {...(small ? { 'Text-14': true } : { 'Text-16': true })} numberOfLines={1} ellipsizeMode="tail" {...primaryColor}>
                {data.name}
              </Text>
              {renderDetails}
            </View>
            {renderAccountBalance()}
          </View>
          {shouldShowGoalsProgress ? (<RowAccountGoalProgress accountId={data.id}/>) : null}
          {!didRenderAccountOwnerInfo && (<View style={styles.spaceUserContainer}>{SpaceOwner}</View>)}
        </View>
      </View>
    </TouchableHighlight>);
    if (isWeb) {
        return <View ref={touchViewRef}>{content}</View>;
    }
    return content;
};
const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    infoContainer: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    infoContainerWithGoal: {
        alignItems: 'flex-start',
    },
    rowAccount: {
        flexDirection: 'row',
        paddingTop: rem(16),
        paddingBottom: rem(16),
        ...(isWeb && {
            paddingHorizontal: 8,
        }),
        alignItems: 'center',
    },
    textContainer: {
        flex: 1,
        justifyContent: 'center',
        paddingHorizontal: rem(16),
    },
    textContainerSmall: {
        paddingLeft: 0,
        paddingRight: rem(16),
    },
    renewButton: {
        paddingHorizontal: rem(20),
    },
    selectWrap: {
        justifyContent: 'center',
        alignItems: 'center',
        height: normalize(34.8),
    },
    buttonContainer: {
        justifyContent: 'center',
    },
    checkOnSquircle: {
        marginRight: rem(8),
        marginLeft: rem(8),
    },
    spaceUserContainer: {
        paddingHorizontal: rem(16),
    },
    spaceUserAvatar: {
        paddingTop: rem(2),
    },
});
export default RowAccount;
